import i18n from "i18next";

import {AuthorizeApiInterface} from "../api/interface/authorize";
import type {RevaApiAuthorizeResult} from "../api/payload-types/authorize";
import {WebError} from "../api/web-error";
import type {AppEnvironment} from "../environment";
import {ErrorI18n} from "../i18n";
import {generateDefaultQueryError} from "../util/errorUtil";
import {isManualPath} from "../util/revaManuals";
import {ALL_REVA_PERMISSIONS} from "../util/revaPermission";
import type {ParsedJwtToken} from "../util/tokenUtil";
import {parseToken} from "../util/tokenUtil";
import {login, logout} from "./authSlice";
import {setError, setIdle, setLoading} from "./loginSlice";
import {resetAll} from "./resetThunk";
import type {RevaThunkAction} from "./revaHooks";
import {clearSpec} from "./specSlice";
import {querySpecsAsync} from "./specThunk";
import type {RevaDispatch, RevaExtraArgs} from "./store";
import {resetValueOrderEntitlements} from "./valueOrderEntitlementsSlice";
import {loadValueOrderEntitlementsThunk} from "./valueOrderEntitlementsThunk";
import {queryCapabilitiesAsync} from "./wmtsThunk";

const KEY_STORAGE_TOKEN = "reva_session_token";

/**
 * i18n keys the authorize error handling produces for a DEFINITIVE auth rejection — i.e. the
 * server actively refused the token (HTTP 401/403). See {@link isTokenRejectedByServer}.
 */
const AUTH_REJECTION_I18N_KEYS = new Set(["login.error.401", "general.error.401", "general.error.403"]);

/**
 * Whether a failed auto-login means the server actively rejected the stored token (401/403) and
 * it must therefore be discarded — as opposed to a transient or interrupted request that leaves
 * the token's validity unknown.
 *
 * The important case to exclude: a full page reload (Vite HMR) aborts the in-flight `/authorize`
 * request, which surfaces as a network error ("TypeError: Failed to fetch") and is wrapped into
 * `ErrorI18n("general.error.invalid")` — NOT an auth rejection. Network failures, timeouts and
 * 5xx must likewise NOT invalidate a still-valid token.
 *
 * Robust to both the raw {@link WebError} and the wrapped {@link ErrorI18n} the authorize
 * interface produces. Defaults to `false` (keep the token); a genuinely invalid token is caught
 * on the next attempt via a real 401.
 */
const isTokenRejectedByServer = (err: unknown): boolean => {
    if (err instanceof WebError) {
        return err.status === 401 || err.status === 403;
    }
    if (err instanceof ErrorI18n) {
        return AUTH_REJECTION_I18N_KEYS.has(err.message);
    }
    return false;
};

const getStoredSession = (isDev: boolean): {token: string; parsedToken: ParsedJwtToken} | null => {
    const token = localStorage.getItem(KEY_STORAGE_TOKEN);
    if (!token) {
        if (isDev) {
            console.log("No stored session token found");
        }
        return null;
    }
    const parsedToken = parseToken(token);
    if (!parsedToken) {
        if (isDev) {
            console.log("Stored session token was not valid");
        }
        clearStoredSession(isDev);
        return null;
    }
    return {token, parsedToken};
};

const clearStoredSession = (isDev: boolean): void => {
    if (isDev) {
        console.log("Clearing stored session token from local storage ...");
    }
    localStorage.removeItem(KEY_STORAGE_TOKEN);
};

const setStoredSession = (isDev: boolean, token: string): void => {
    const parsedToken = parseToken(token);
    if (!parsedToken) {
        if (isDev) {
            console.warn("Could not store JWT token: was not valid or empty");
        }
        return;
    }
    localStorage.setItem(KEY_STORAGE_TOKEN, token);
    if (isDev) {
        console.log(`Storing JWT token for user ${parsedToken.user} with expire date: ${parsedToken.expireDate}`);
    }
};

const loginResponseFlow = (result: RevaApiAuthorizeResult, env: AppEnvironment, dispatch: RevaDispatch): void => {
    if (!result.jwt) {
        throw new Error("No token was generated on login!");
    }
    setStoredSession(env.isDevelopmentMode, result.jwt);
    dispatch(setIdle());
    dispatch(
        login({
            authData: {
                username: result.username,
                name: result.name ?? undefined,
                license: result.license,
                licenseId: result.licenseId,
                licenseDisplayName: result.licenseDisplayName ?? undefined,
                email: result.email ?? null,
                emailVerified: result.emailVerified ?? false,
                emailVerificationHint: result.emailVerificationHint ?? false,
                showEvaPlusLinks: result.showEvaPlusLinks,
            },
            token: result.jwt,
            permissions: result.permissionNames ?? [],
        }),
    );
    if (env.isDevelopmentMode) {
        const validPermissions = new Set<string>(ALL_REVA_PERMISSIONS);
        result.permissionNames
            .filter((p) => !validPermissions.has(p))
            .forEach((p) => {
                console.warn(`Unknown permission name received from API: '${p}'`);
            });
    }

    // #19351: the stored account language wins over the browser-detected one — only switch when it
    // actually differs, so an already-matching UI language is not needlessly reloaded.
    if (result.language && result.language !== i18n.language) {
        void i18n.changeLanguage(result.language);
    }

    // #19302: Die Handbuch-Seite braucht nur das Token; „REVA starten“ lädt die Seite dort neu und damit alles Weitere.
    if (isManualPath(window.location.pathname)) {
        return;
    }

    void dispatch(querySpecsAsync());
    void dispatch(queryCapabilitiesAsync());
    // Parallel zu den statischen Daten: fehlt dem Auftraggeber die Auftragsart REVA, zeigt AppContainer statt der
    // App eine Fehlerseite. Spätere Aufrufe (Beauftragen-Panel) treffen dieselbe Antwort im Slice.
    void dispatch(loadValueOrderEntitlementsThunk());
};

/**
 * Thunk to authenticate/login the user based on stored credentials.
 */
export const tryPerformInitialLogin =
    (): RevaThunkAction<Promise<void>> =>
    async (dispatch, _getState, {env, serviceApi}: RevaExtraArgs) => {
        if (env.isDevelopmentMode) {
            console.log("Trying to perform initial login from stored session ...");
        }
        const initialToken = getStoredSession(env.isDevelopmentMode);
        if (!initialToken) {
            if (env.isDevelopmentMode) {
                console.log("No (valid) stored session token found.");
            }
            dispatch(setIdle());
            return;
        }
        if (initialToken.parsedToken.expired) {
            if (env.isDevelopmentMode) {
                console.log(`Stored session token is expired since ${initialToken.parsedToken.expireDate.toString()}`);
            }
            clearStoredSession(env.isDevelopmentMode);
            dispatch(setError({error: "login.error.expired", errorHasContactLink: false}));
            return;
        }

        try {
            if (env.isDevelopmentMode) {
                console.log(`Found initial stored token - try to auto-login '${initialToken.parsedToken.user}' ...`);
            }
            const result = await new AuthorizeApiInterface({token: initialToken.token}).execute(serviceApi);
            loginResponseFlow(result, env, dispatch);
        } catch (err) {
            // Only a definitive server rejection (401/403) means the stored token is invalid and
            // should be discarded. A transient or interrupted request — most importantly an
            // in-flight /authorize request aborted by a full page reload (Vite HMR) — must NOT
            // delete a still-valid token, otherwise the user is logged out on reload. The cause
            // is still recognized and logged; only the destructive reaction is dropped.
            const rejected = isTokenRejectedByServer(err);
            if (env.isDevelopmentMode) {
                console.log(
                    rejected
                        ? `Stored token for '${initialToken.parsedToken.user}' was rejected by the server - clearing session.`
                        : `Auto-login for '${initialToken.parsedToken.user}' did not complete (transient/interrupted request, e.g. a page reload) - keeping the stored token.`,
                    err,
                );
            }
            if (rejected) {
                clearStoredSession(env.isDevelopmentMode);
                dispatch(setError({error: "login.error.expired", errorHasContactLink: false}));
            } else {
                // Keep the token: the next load (or a manual retry) re-authenticates with it.
                dispatch(setError({error: "login.error.unavailable", errorHasContactLink: false}));
            }
        }
    };

/**
 * Thunk to authenticate/login a user.
 */
export const tryPerformLoginAsync =
    (username: string, password: string): RevaThunkAction<Promise<void>> =>
    async (dispatch, _getState, {env, serviceApi}: RevaExtraArgs) => {
        const authorizeInterface = new AuthorizeApiInterface({username, password});
        if (!authorizeInterface.validateAndDispatchQueryError((queryError) => dispatch(setError(queryError)))) {
            return;
        }
        dispatch(setLoading());
        try {
            if (env.isDevelopmentMode) {
                console.log(`Performing login with username ${username}`);
            }
            const result = await authorizeInterface.execute(serviceApi);
            loginResponseFlow(result, env, dispatch);
        } catch (err) {
            dispatch(setError(generateDefaultQueryError(err as Error)));
        }
    };

/**
 * Thunk to logout a user.
 */
export const performLogout =
    (): RevaThunkAction<Promise<void>> =>
    async (dispatch, _getState, {env}: RevaExtraArgs) => {
        clearStoredSession(env.isDevelopmentMode);
        dispatch(logout());
        dispatch(clearSpec());
        dispatch(resetAll());
        // Nicht in resetAll: die Freischaltungen hängen an der Lizenz, nicht an der geladenen Bewertung.
        dispatch(resetValueOrderEntitlements());
    };
